import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import TeamVacationsWidget from "@/components/TeamVacationsWidget";
import TeamCelebrationWidget from "@/components/TeamCelebrationWidget";
import VacationQuotaReport from "@/components/VacationQuotaReport";
import KanbanBoard from "@/components/KanbanBoard";
import TemplateManager from "@/components/TemplateManager";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Trello, Plane, Layers } from "lucide-react";

const BOARDS = [
  { board_id: "avgs", label: "AVGS-Coaches" },
  { board_id: "we_academy", label: "We Academy Team" },
  { board_id: "weiterbildung", label: "Weiterbildung" },
];

export default function TeamProjectmanagementPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const isAdmin = user?.is_master_admin || user?.role === "admin" || user?.role === "manager" || user?.waat_role === "backoffice";
  const [activeBoard, setActiveBoard] = useState(searchParams.get("board") || BOARDS[0].board_id);
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") === "vacation" ? "vacation" : "boards");
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const deepLinkCardId = searchParams.get("card") || null;

  useEffect(() => {
    if (searchParams.get("board")) setActiveBoard(searchParams.get("board"));
    if (searchParams.get("tab")) setActiveTab(searchParams.get("tab"));
  }, [searchParams]);

  return (
    <div className="px-8 py-10 lg:px-12 lg:py-12 max-w-[1600px] mx-auto" data-testid="team-pm-page">
      <div className="flex items-end justify-between mb-8 border-b border-slate-200 pb-8 flex-wrap gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-overline text-slate-500">Team</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-light tracking-tighter text-slate-900 mt-2">Erfolg, Boards &amp; Urlaub</h1>
          <p className="text-sm text-slate-500 mt-3 max-w-2xl">
            Der Team-Erfolg auf einen Blick, Trello-ähnliche Boards zum Zuweisen und Verschieben von Aufgaben,
            sowie Urlaubs- und Abwesenheitsplanung.
          </p>
        </div>
      </div>

      <div className="mb-8" data-testid="team-celebration-widget">
        <ErrorBoundary label="Team-Erfolg">
          <TeamCelebrationWidget />
        </ErrorBoundary>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-6 bg-slate-100">
          <TabsTrigger data-testid="tab-boards" value="boards" className="flex items-center gap-2">
            <Trello size={14} strokeWidth={1.5} /> Boards
          </TabsTrigger>
          <TabsTrigger data-testid="tab-vacation" value="vacation" className="flex items-center gap-2">
            <Plane size={14} strokeWidth={1.5} /> Urlaub
          </TabsTrigger>
        </TabsList>

        <TabsContent value="boards">
          <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
            <div className="flex gap-2">
              {BOARDS.map((b) => (
                <button
                  key={b.board_id}
                  data-testid={`board-switch-${b.board_id}`}
                  onClick={() => setActiveBoard(b.board_id)}
                  className={`px-4 py-2 text-sm font-medium border transition-colors ${
                    activeBoard === b.board_id
                      ? "bg-slate-900 text-white border-slate-900"
                      : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                  }`}
                >
                  {b.label}
                </button>
              ))}
            </div>
            {isAdmin && (
              <button onClick={() => setTemplatesOpen(true)} data-testid="open-template-manager-button"
                className="flex items-center gap-1.5 text-xs font-medium text-slate-500 border border-slate-200 px-3 py-2 hover:border-slate-400 hover:text-slate-900">
                <Layers size={13} strokeWidth={1.5} /> Vorlagen verwalten
              </button>
            )}
          </div>
          <ErrorBoundary label="Board">
            <KanbanBoard boardId={activeBoard} key={activeBoard} initialCardId={activeBoard === (searchParams.get("board") || BOARDS[0].board_id) ? deepLinkCardId : null} />
          </ErrorBoundary>
        </TabsContent>

        <TabsContent value="vacation">
          <ErrorBoundary label="Urlaubs-Übersicht">
            <TeamVacationsWidget currentUser={user} />
          </ErrorBoundary>

          {isAdmin && (
            <div className="mt-8">
              <ErrorBoundary label="Kontingent-Report">
                <VacationQuotaReport />
              </ErrorBoundary>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {isAdmin && (
        <Dialog open={templatesOpen} onOpenChange={setTemplatesOpen}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="template-manager-dialog">
            <DialogHeader>
              <DialogTitle>Kartenvorlagen verwalten</DialogTitle>
            </DialogHeader>
            <TemplateManager />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
